import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { getCurrentUser } from '../../../selectors/userSelectors';
import { CalendarIcon } from './CalendarIcon';
import { SessionDetails } from './ScheduleSessionForm/constants';
import { formatFullDate, formatTime } from '../../../helpers/dateHelpers';
import { InsuranceStatus } from '../../../constants';

export interface Props {
  sessionDetails: SessionDetails;
}

const PSYCHIATRY_SESSION_TITLES = {
  60: 'initial evaluation',
  30: 'follow-up',
};

const DEFAULT_SESSION_TITLE = 'session';

const ScheduleSessionConfirmation: React.FC<Props> = ({ sessionDetails }) => {
  const currentUser = useSelector(getCurrentUser);
  const { showHpiClientStatusInfo } = useFlags();

  const sessionTitle = useMemo(() => {
    const { member, sessionDuration } = sessionDetails;

    if (sessionDuration && currentUser.isPsychiatrist) {
      return `${PSYCHIATRY_SESSION_TITLES[sessionDuration]} ${DEFAULT_SESSION_TITLE}`;
    }

    const { isCompanyHpiEnabled, memberInsuranceStatus, visitsRemaining } = member;

    if (
      currentUser.isTherapist &&
      showHpiClientStatusInfo &&
      isCompanyHpiEnabled &&
      memberInsuranceStatus === InsuranceStatus.ELIGIBLE &&
      visitsRemaining === 0
    ) {
      return `extended therapy ${DEFAULT_SESSION_TITLE}`;
    }

    return DEFAULT_SESSION_TITLE;
  }, [currentUser, sessionDetails, showHpiClientStatusInfo]);

  return (
    <div className="text-center p-4">
      <div className="mb-6 flex justify-center">
        <CalendarIcon date={sessionDetails.startTime} />
      </div>

      <div className="font-black text-black text-2xl mb-4">
        {sessionDetails.member.memberFirstName} {sessionDetails.member.memberLastName}'s{' '}
        {sessionTitle} has been scheduled
      </div>

      <div className="font-black text-gray-800 text-base mb-8">
        {`${formatFullDate(sessionDetails.startTime, {
          includeWeekday: true,
        })} at ${formatTime(sessionDetails.startTime)} - ${formatTime(sessionDetails.endTime, {
          includeTimezone: true,
        })}`}
      </div>

      <div className="font-normal text-gray-700 text-sm mx-8 mb-4">
        If you need to reschedule or cancel your session, let your clients know at least{' '}
        <span className="font-bold">24 hours in advance.</span>
      </div>
    </div>
  );
};

export default ScheduleSessionConfirmation;
